import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
} from '@mui/material';

const TSHIRT_SIZES = [
  { value: 'XS', label: 'XS (Extra Small)' },
  { value: 'S', label: 'S (Small)' },
  { value: 'M', label: 'M (Medium)' },
  { value: 'L', label: 'L (Large)' },
  { value: 'XL', label: 'XL (Extra Large)' },
  { value: 'XXL', label: 'XXL (2X Large)' },
  { value: 'XXXL', label: 'XXXL (3X Large)' },
];

/**
 * TShirtSizeDialog Component
 * Displays a dialog to collect user's t-shirt size preference.
 * This dialog cannot be dismissed until the user submits their size.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onSubmit - Function to call when size is submitted
 * @param {boolean} props.loading - Whether the submission is in progress
 * @param {string} props.error - Error message to display
 * @returns {React.ReactElement} T-shirt size dialog component
 */
const TShirtSizeDialog = ({
  open,
  onSubmit,
  loading = false,
  error = null,
}) => {
  const [selectedSize, setSelectedSize] = useState('');

  const handleSizeChange = (event) => {
    setSelectedSize(event.target.value);
  };

  const handleSubmit = () => {
    if (selectedSize) {
      onSubmit(selectedSize);
    }
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="tshirt-size-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: '8px',
          minWidth: '400px',
          maxWidth: '500px',
        },
      }}
    >
      <DialogTitle id="tshirt-size-dialog-title">
        <Typography variant="h5" component="div" fontWeight="bold">
          Select Your T-Shirt Size
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please select your preferred t-shirt size. This information is required
            and will be used for company merchandise.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'medium' }}>
              T-Shirt Size
            </FormLabel>
            <RadioGroup
              aria-label="t-shirt size"
              name="tshirt-size"
              value={selectedSize}
              onChange={handleSizeChange}
            >
              {TSHIRT_SIZES.map((size) => (
                <FormControlLabel
                  key={size.value}
                  value={size.value}
                  control={<Radio />}
                  label={size.label}
                  sx={{
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!selectedSize || loading}
          fullWidth
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Saving...' : 'Save T-Shirt Size'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TShirtSizeDialog;
